import React, { useState, useMemo, useCallback } from "react";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";
import Table from "components/Tables/StatTable.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import {StatHeaders, StatNutroscanHeaders, convertDataIntoTableData,} from "../../utils/table.js";
import StatisticsReport from "components/StatisticsReport/StatisticsReport";
import { getReadingPdfReport } from "spectral-core/api";
import RecalculateReadingsModal from "components/RecalculateReadingsModal/RecalculateReadingsModal";
import ReadingDetailsModal from "components/ReadingDetailsModal/ReadingDetailsModal";
import { useEffect } from "react";
import axios from "axios";
import { getModels } from "spectral-core/api";
import { useRootDispatch } from "hooks/reduxHooks";
import { logoutAction } from "store/user/user.actions";
import { useHistory } from "react-router-dom";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";

//@ts-ignore
import lodash from "lodash";

import { ChartData } from "types/chartData";

import StatisticsHandler from "../../statisticsModels/StatisticsHandler.js";
import SpinLoading from "components/Loading/SpinLoading.js";
import intl from "react-intl-universal";

import Reading from "spectral-core/models/Reading";

import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { useRootSelector } from "hooks/reduxHooks";

import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import Chart from "components/Chart/Chart";
import { cultureTypeLabelPt } from "spectral-core/cultureEnum";
import {
  filterNutroScanReadings,
  aggregateNutroScanOverview,
} from "utils/nutroScanAggregates"

//@ts-ignore
const useStyles = makeStyles(styles);

function createChartData(readings: Reading[]) {
  if (readings.length === 0) {
    return undefined;
  }

  //@ts-ignore
  let predicteds = lodash.map(readings, (item) => {
    return item != null && item.predictedValues != null
      ? JSON.parse(item.predictedValues)
      : [];
  });

  predicteds = lodash.flattenDeep(
    //@ts-ignore
    predicteds.filter((predicted) => predicted != null)
  );

  if (predicteds.length === 0) return undefined;

  const statisticsHandler = new StatisticsHandler(predicteds);
  return statisticsHandler.createAnalysis() as ChartData;
}

//@ts-ignore
export default function Statistics() {
  const classes = useStyles();

  const userAccessLevel = useRootSelector(
    (state) => state.user.currentUser?.accessLevel
  );

  const userCompany = useRootSelector(
    (state) => state.user.currentUser?.company
  );

  const canGenerateReports =
  userCompany === "ICL" || userAccessLevel === 9000;

  const accessToken = useRootSelector(
    (state) => state.user.currentToken?.accessToken
  );

  const dispatch = useRootDispatch();
  const history = useHistory();

  const [models, setModels] = useState<any[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  const [nutroScanFilterEnabled, setNutroScanFilterEnabled] = useState(false);

  useEffect(() => {
    async function loadModels() {
      if (!accessToken) return;

      setModelsLoading(true);

      try {
        const apiModels = await getModels(accessToken);

        setModels(apiModels);
      } catch (err) {
        if (
          axios.isAxiosError(err) &&
          (err.response?.status === 401 ||
            err.response?.status === 403)
        ) {
          dispatch(logoutAction());
          history.push("/");
        }

        console.error(err);
      } finally {
        setModelsLoading(false);
      }
    }

    loadModels();
  }, [accessToken, dispatch, history]);

  const tableHeaders = [
  ...StatNutroscanHeaders,
  {
    title: "",
    field: "actions",
    filtering: false,
    sorting: false,
    searchable: false,
    export: false,
    cellStyle: {
      width: 60,
      textAlign: "center",
    },
    headerStyle: {
      width: 60,
    },
    render: (rowData: any) => (
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();

          const reading = filteredReadings[rowData.tableData.index];
          if (!reading) return;

          setSelectedReading(reading);
          setDetailsModalOpen(true);
        }}
      >
        <MoreVertIcon />
      </IconButton>
    ),
  },
];

  const [selectedReadings, setSelectedReadings] = useState<Reading[]>([]);

  const hasDifferentReadingCultures =  selectedReadings.length > 1 && 
  new Set(
    selectedReadings
      .map((r) => r.culture)
      .filter(Boolean)
  ).size > 1;

  const isLoggedIn = useUserLoggedIn();

  const { isFetching } = useGetReadings();

  const readings = useRootSelector((state) => state.reading.readings);

  const chartData = useMemo(
    () => createChartData(selectedReadings),
    [selectedReadings]
  );

  const loading = !isLoggedIn || readings === undefined;
  const actionsDisabled =
  loading || isFetching;

  // Filtros globais — mesmo formato da página NutroScan
  const [filterDiretoria, setFilterDiretoria] = useState<string>("");
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterCulture, setFilterCulture] = useState<string>("");
  const [filterName, setFilterName] = useState<string>("");
  const [filterSampleCode, setFilterSampleCode] = useState<string>("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");

  const baseReadings = useMemo(
    () => (readings !== undefined ? readings : []),
    [readings]
  );

  const filteredBaseReadings = useMemo(
    () =>
      nutroScanFilterEnabled
        ? filterNutroScanReadings(baseReadings, true)
        : baseReadings,
    [baseReadings, nutroScanFilterEnabled]
  );

  const availableDiretorias = useMemo(() => {
  const set = new Set<string>();

  for (const r of filteredBaseReadings) {
    const diretoria = (r.userSite || "").trim();

    if (diretoria) {
      set.add(diretoria);
    }
  }

  return Array.from(set).sort();
  }, [filteredBaseReadings]);

  // Filtro em cascata: cada lista respeita os filtros anteriores
  const afterDiretoria = useMemo(() => {
    if (!filterDiretoria) return filteredBaseReadings;

    return filteredBaseReadings.filter(
      (r) => (r.userSite || "").trim() === filterDiretoria
    );
  }, [filteredBaseReadings, filterDiretoria]);

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterDiretoria) {
      const user = (r.userName || "").trim();
      if (user) set.add(user);
    }
    return Array.from(set).sort();
  }, [afterDiretoria]);

  const afterUser = useMemo(() => {
    if (filterUser.length === 0) return afterDiretoria;
    return afterDiretoria.filter((r) => filterUser.includes((r.userName || "").trim()));
  }, [afterDiretoria, filterUser]);

  const availableCultures = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterUser) {
      const label = cultureTypeLabelPt(r.culture, r.customCultureName) || "—";
      set.add(label);
    }
    return Array.from(set).sort();
  }, [afterUser]);

  const afterCulture = useMemo(() => {
    if (!filterCulture) return afterUser;
    return afterUser.filter(
      (r) => cultureTypeLabelPt(r.culture, r.customCultureName) === filterCulture
    );
  }, [afterUser, filterCulture]);

  const filteredReadings = useMemo(() => {
  let filtered = afterCulture;
    if (filterName.trim()) {
  const searchName = filterName.trim().toLowerCase();

  filtered = filtered.filter((r) =>
    (r.name || "").toLowerCase().includes(searchName)
  );
}

if (filterSampleCode.trim()) {
  const searchSampleCode = filterSampleCode.trim().toLowerCase();

  filtered = filtered.filter((r) =>
    (r.sampleCode || "").toLowerCase().includes(searchSampleCode)
  );
}
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return filtered;
  }, [afterCulture, filterName, filterSampleCode, filterDateFrom, filterDateTo]);

  const tableData = useMemo(
    () => convertDataIntoTableData(filteredReadings),
    [filteredReadings]
  );

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedReading, setSelectedReading] = useState<Reading | null>(null);

  const handleSelectionCallback = useCallback(
    (rows: any[]) => {
      const selected = rows
        .map((row) => filteredReadings[row.tableData.index])
        .filter(Boolean);
      setSelectedReadings(selected);
    },
    [filteredReadings]
  );

  const table = useMemo(() => {
    return loading ? (
      <SpinLoading />
    ) : (
      <Table
        headers={tableHeaders}
        tableData={tableData}
        SelectionChange={handleSelectionCallback}
      />
    );
  }, [loading, handleSelectionCallback, tableData]);

  const handleGeneratePdf = async () => 
  {
    if (!accessToken) return;
    if (selectedReadings.length !== 1) return;

    const sampleCode = selectedReadings[0].sampleCode;
    if (!sampleCode) {
      alert(intl.get("alerts.pdfDenied"));
      return;
    }

    const sampleName = selectedReadings[0].name;

    try {
      const pdfBlob = await getReadingPdfReport(accessToken, sampleCode);

      const url = window.URL.createObjectURL(pdfBlob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${sampleName}`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erro ao gerar relatório PDF", err);
      alert(intl.get("alerts.pdfError"));
    }
  };

  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);

  const handleOpenRecalculateModal = () => {
    if (hasDifferentReadingCultures) {
      alert(intl.get("alerts.differentCulturesError"));
      return;
    }

    setRecalculateModalOpen(true);
  };

  return (
    <div>
      <GridContainer alignItems="center" spacing={2}>
        <GridItem>
          <StatisticsReport
            selectedReadings={selectedReadings}
            statisticsData={chartData}
            isFetching={isFetching}
          />
        </GridItem>

        <GridItem>
          {canGenerateReports && (
            <div style={{ display: "flex", gap: 32 }}>
              <button
                className="reportButtom reportButtomPdf"
                onClick={handleGeneratePdf}
                disabled={actionsDisabled || selectedReadings.length !== 1}
              >
                {isFetching ? intl.get("btn.loading") : intl.get("btn.generatePDF")}
              </button>

              <button
                className="reportButtom runModelButton"
                onClick={handleOpenRecalculateModal}
                disabled={actionsDisabled || selectedReadings.length === 0}
              >
                {isFetching ? intl.get("btn.loading") : intl.get("btn.recalculate")}
              </button>
            </div>
          )}
        </GridItem>
        <FormControlLabel
          control={
            <Switch
              checked={nutroScanFilterEnabled}
              onChange={(e) => {
                setNutroScanFilterEnabled(e.target.checked);
                setFilterDiretoria("");
                setFilterUser([]);
                setFilterCulture("");
                setFilterName("");
                setFilterSampleCode("");
                setFilterDateFrom("");
                setFilterDateTo("");
              }}
              color="primary"
            />
          }
          label="Apenas Médias"
        />
      </GridContainer>
      {/* Filtros globais — mesmo formato da página NutroScan */}
      <Paper style={{ padding: "12px 16px", marginTop: 12, marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <FormControl style={{ minWidth: 140, flex: "1 1 140px" }} size="small">
          <InputLabel id="filter-diretoria" shrink>Diretoria</InputLabel>
          <Select
            labelId="filter-diretoria"
            value={filterDiretoria}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterDiretoria(e.target.value as string);
              setFilterUser([]);
              setFilterCulture("");
              setFilterName("");
              setFilterSampleCode("");
            }}
            displayEmpty
          >
            <MenuItem value="">Todas</MenuItem>
            {availableDiretorias.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
          <InputLabel id="filter-user" shrink>Usuário</InputLabel>
          <Select
            labelId="filter-user"
            multiple
            value={filterUser}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterUser(e.target.value as string[]);
              setFilterCulture("");
              setFilterName("");
              setFilterSampleCode("");
            }}
            displayEmpty
            renderValue={(selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return "Todos";
              if (sel.length === 1) return sel[0];
              return `${sel.length} usuários`;
            }}
          >
            {availableUsers.map((u) => (
              <MenuItem key={u} value={u}>
                <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                <ListItemText primary={u} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 140, flex: "1 1 140px" }} size="small">
          <InputLabel id="filter-culture" shrink>Cultura</InputLabel>
          <Select
            labelId="filter-culture"
            value={filterCulture}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterCulture(e.target.value as string);
              setFilterName("");
              setFilterSampleCode("");
            }}
            displayEmpty
          >
            <MenuItem value="">Todas</MenuItem>
            {availableCultures.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Nome da leitura"
          size="small"
          value={filterName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterName(e.target.value)
          }
          style={{ minWidth: 160, flex: "1 1 160px" }}
        />
        <TextField
          label="Cód. amostra"
          size="small"
          value={filterSampleCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterSampleCode(e.target.value)
          }
          style={{ minWidth: 160, flex: "1 1 160px" }}
        />
        <TextField
          label="De"
          type="date"
          size="small"
          value={filterDateFrom}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 145, flex: "0 0 145px" }}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          value={filterDateTo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 145, flex: "0 0 145px" }}
        />
        {(filterDiretoria || filterUser.length > 0 || filterCulture || filterName ||filterSampleCode || filterDateFrom || filterDateTo) && (
          <Button
            size="small"
            onClick={() => {
              setFilterDiretoria("");
              setFilterUser([]);
              setFilterCulture("");
              setFilterName("");
              setFilterSampleCode("");
              setFilterDateFrom("");
              setFilterDateTo("");
            }}
          >
            Limpar filtros
          </Button>
        )}
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto", textAlign: "right" }}>
          {filteredReadings.length} leituras
        </Typography>
      </Paper>

      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>
                {intl.get("statistics.reads")}
              </h4>
            </CardHeader>
            <CardBody>{table}</CardBody>
          </Card>
        </GridItem>
        {/* Gráficos removidos por solicitação ICL */}
      </GridContainer>
      <RecalculateReadingsModal
        open={recalculateModalOpen}
        onClose={() => setRecalculateModalOpen(false)}
        selectedReadings={selectedReadings}/>

      <ReadingDetailsModal
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          reading={selectedReading}
          models={models}
          modelsLoading={modelsLoading}
      />
    </div>
  );
}
